import React from 'react';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  EyeOff
} from 'lucide-react';

import { cn } from "../../lib/utils";

// shadcn UI
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

import { Progress } from "../ui/progress";

interface PlatformStatus {
  platformId: string;
  platformName: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  errorMessage?: string;
  platformType?: string;
}

interface UploadProgressDialogProps {
  open: boolean;
  platforms: PlatformStatus[];
  onClose?: () => void;
  isDryRun?: boolean;
  totalProgress?: number;
  message?: string;
  isPublishing?: boolean; // New prop for when server upload is done and background publishing starts
}

const PLATFORM_NAMES: Record<string, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  threads: 'Threads',
  x: 'X (Twitter)',
  youtube: 'YouTube',
  bluesky: 'Bluesky',
  pinterest: 'Pinterest',
};

export default function UploadProgressDialog({
  open,
  platforms,
  onClose,
  isDryRun = false,
  totalProgress,
  message,
  isPublishing = false,
}: UploadProgressDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className={cn(
        "bg-card border-border text-foreground sm:max-w-xl p-0 overflow-hidden",
        isDryRun && "border-amber-500/50"
      )}>
        {totalProgress !== undefined && totalProgress < 100 && !isPublishing && (
          <div className="absolute top-0 left-0 right-0 z-50">
            <Progress value={totalProgress} className="h-1 rounded-none bg-primary/10 [&>div]:bg-primary transition-all duration-300" />
          </div>
        )}
        
        <div className="p-12 flex flex-col items-center justify-center space-y-6">
          {isPublishing ? (
            <div className="flex flex-col items-center gap-4 animate-in zoom-in-95 duration-300 w-full">
              <div className="h-20 w-20 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center shadow-[0_0_30px_hsl(var(--primary)/0.1)]">
                <CheckCircle2 className="h-10 w-10 text-green-500" strokeWidth={3} />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-black text-white uppercase tracking-tighter">Post Uploaded!</h3>
                <p className="text-sm text-primary font-bold italic animate-pulse">
                  Publishing now...
                </p>
              </div>
            </div>
          ) : (
            <div className="w-full">
              <div className="flex flex-col items-center gap-8">
                <div className="relative">
                  <div className="h-32 w-32 rounded-full border-4 border-primary/10 border-t-primary animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="h-10 w-10 text-primary animate-pulse" />
                  </div>
                </div>
                
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground font-black uppercase tracking-[0.3em] animate-pulse">
                    {message || "Uploading video..."}
                  </p>
                  {totalProgress !== undefined && (
                    <p className="text-3xl font-black text-white tracking-tighter">
                      {totalProgress}%
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
